import { HttpError } from 'react-admin';
import { ErrorData } from 'types/embedded/error';
import { getMessageFromErrorBody } from 'utils/getMessageFromErrorBody';

const FALLBACK_MESSAGE = 'Неизвестная ошибка';

/**
 * @api-platform/admin реджектит ошибки в двух разных формах:
 *
 * 1. HttpError — когда сервер ответил валидным JSON-LD/problem+json
 *    (например, 422 с ошибками валидации полей). Тело уже распарсено
 *    библиотекой и лежит в error.body.
 *
 * 2. { response: Response } — когда статус >= 500, либо контент не JSON
 *    и response не ok. В этом случае библиотека НЕ читает тело сама,
 *    и мы обязаны прочитать response.json() сами (и только один раз).
 *
 * См. https://github.com/api-platform/api-doc-parser/blob/main/src/hydra/fetchJsonLd.ts
 */

const isRawResponseError = (error: unknown): error is { response: Response } =>
  typeof error === 'object' &&
  error !== null &&
  'response' in error &&
  (error as { response?: unknown }).response instanceof Response;

const getMessageFromHttpError = (error: HttpError): string => {
  const body = error.body as ErrorData | undefined;
  return getMessageFromErrorBody(body ?? null) || error.message || FALLBACK_MESSAGE;
};

const getMessageFromRawResponse = async (response: Response): Promise<string> => {
  const body: ErrorData | null = await response.json().catch((parseError) => {
    console.error('Failed to parse error response body', parseError);
    return null;
  });

  return getMessageFromErrorBody(body) || response.statusText || FALLBACK_MESSAGE;
};

export const getServerErrorMessage = async (error: unknown): Promise<string> => {
  if (error instanceof HttpError) {
    return getMessageFromHttpError(error);
  }

  if (isRawResponseError(error)) {
    return getMessageFromRawResponse(error.response);
  }

  if (error instanceof Error) {
    return error.message || FALLBACK_MESSAGE;
  }

  return FALLBACK_MESSAGE;
};
